import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { NO_FILL } from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import {
  DrawType,
  LineType,
  MarkupTool,
  ShapeKind,
} from '~/client/src/shared/components/MapMarkups/markupTools'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { getImageDimensions } from '~/client/src/shared/utils/fileValidationHelper'
import {
  claimKeyboard,
  KeyboardClaim,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'
import { toast } from '~/client/src/shared/utils/toast'

import {
  clampViewport,
  fitSize,
  fitViewport,
  fitWithin,
  IViewport,
  markupBounds,
  markupUnit,
  MeasureText,
  rectCorners,
  toImagePoint,
  toScreenPoint,
  zoomViewport,
} from './photoMarkupGeometry'
import PhotoMarkupGestures from './PhotoMarkupGestures'
import PhotoMarkupHistory from './PhotoMarkupHistory'
import { handlePhotoMarkupKey } from './photoMarkupKeyboard'
import {
  defaultArrowheads,
  hasContent,
  IPathMarkup,
  IPoint,
  IRect,
  ISize,
  isTextMarkup,
  isVertexEdited,
  ITextMarkup,
  PhotoMarkup,
} from './photoMarkupModel'
import { IScene, renderPhoto } from './photoMarkupRender'
import PhotoMarkupStyle from './PhotoMarkupStyle'

export interface IPhotoMarkupDeps {
  fileName: string
  isMobileEnv: boolean
  onSave(file: File): Promise<void>
  measureText?: MeasureText
  createCanvas?: (size: ISize) => HTMLCanvasElement
}

export interface IScreenAnchor {
  x: number
  top: number
  bottom: number
}

async function readImageSize(blob: Blob): Promise<ISize> {
  const url = URL.createObjectURL(blob)
  try {
    return await getImageDimensions(url)
  } finally {
    URL.revokeObjectURL(url)
  }
}

export default class PhotoMarkupStore {
  @observable public activeTool: MarkupTool = 'select'
  @observable public shapeKind: ShapeKind = 'rectangle'
  @observable public lineType: LineType = 'line'
  @observable public drawType: DrawType = 'pen'
  @observable.ref public markups: PhotoMarkup[] = []
  @observable public selectedId: string | null = null
  @observable public editingId: string | null = null
  @observable.ref public stageSize: ISize = { width: 0, height: 0 }
  @observable.ref public availableStageSize: ISize | null = null
  @observable.ref public imageSize: ISize | null = null
  @observable.ref public image: CanvasImageSource | null = null
  @observable public hasLoadFailed = false
  @observable public isSaving = false
  @observable private frozenUnit: number | null = null
  @observable.ref private zoom: IViewport | null = null

  public readonly history = new PhotoMarkupHistory(() => this.markups)
  public readonly gestures = new PhotoMarkupGestures(this)
  public readonly style = new PhotoMarkupStyle(this)
  private keyboardClaim: KeyboardClaim | null = null
  private isDisposed = false
  private measureContext: CanvasRenderingContext2D | null = null

  public constructor(private readonly deps: IPhotoMarkupDeps) {
    makeObservable(this)
  }

  public get isMobileEnv(): boolean {
    return this.deps.isMobileEnv
  }

  @computed
  public get view(): IViewport | null {
    if (!this.imageSize || !this.stageSize.width || !this.stageSize.height) {
      return null
    }
    return this.zoom
      ? clampViewport(this.zoom, this.stageSize, this.imageSize)
      : fitViewport(this.stageSize, this.imageSize)
  }

  @computed
  public get isZoomed(): boolean {
    return (
      !!this.view &&
      this.view.scale > fitViewport(this.stageSize, this.imageSize).scale
    )
  }

  @computed
  public get stageFrame(): ISize | null {
    const available = this.availableStageSize
    if (!available || !this.imageSize || this.isMobileEnv) return available
    return fitSize(available, this.imageSize)
  }

  @computed
  public get unit(): number {
    return this.frozenUnit ?? 1
  }

  @computed
  public get selected(): PhotoMarkup | null {
    return this.markups.find(m => m.id === this.selectedId) ?? null
  }

  @computed
  public get selectedPath(): IPathMarkup | null {
    const selected = this.selected
    return selected && !isTextMarkup(selected) ? selected : null
  }

  @computed
  public get selectedText(): ITextMarkup | null {
    const selected = this.selected
    return selected && isTextMarkup(selected) ? selected : null
  }

  @computed
  public get editingText(): ITextMarkup | null {
    const selected = this.selectedText
    return selected?.id === this.editingId ? selected : null
  }

  @computed
  public get hasChanges(): boolean {
    return this.markups.some(hasContent) || this.gestures.hasPendingMarkup
  }

  @computed
  public get polygonDrawing(): boolean {
    return this.activeTool === 'shape' && !!this.gestures.vertices.length
  }

  @computed
  public get polylineDrawing(): boolean {
    return this.activeTool === 'arrow' && !!this.gestures.vertices.length
  }

  @computed
  public get isIdle(): boolean {
    return (
      this.activeTool === 'select' &&
      !this.selectedId &&
      !this.gestures.isActive
    )
  }

  @computed
  public get selectionScreenBox(): IRect | null {
    const view = this.view
    const selected = this.selected
    if (!view || !selected) return null
    const bounds = markupBounds(selected, this.unit, this.measure)
    const topLeft = toScreenPoint({ x: bounds.minX, y: bounds.minY }, view)
    const bottomRight = toScreenPoint({ x: bounds.maxX, y: bounds.maxY }, view)
    return {
      minX: topLeft.x,
      minY: topLeft.y,
      maxX: bottomRight.x,
      maxY: bottomRight.y,
    }
  }

  @computed
  public get selectionHandles(): IPoint[] {
    const view = this.view
    const selected = this.selected
    const box = this.selectionScreenBox
    if (!view || !selected || !box || this.editingId) return []
    if (isVertexEdited(selected)) {
      return (selected as IPathMarkup).points.map(p => toScreenPoint(p, view))
    }
    return rectCorners(box)
  }

  @computed
  public get toolbarAnchor(): IScreenAnchor | null {
    const box = this.selectionScreenBox
    if (!box || this.gestures.interacting) return null
    return { x: (box.minX + box.maxX) / 2, top: box.minY, bottom: box.maxY }
  }

  @computed
  public get scene(): IScene | null {
    const { view, imageSize, selectionScreenBox, editingId } = this
    if (!view || !imageSize || this.frozenUnit == null) return null
    return {
      view,
      imageSize,
      unit: this.unit,
      markups: this.markups.filter(m => m.id !== editingId),
      preview: this.gestures.preview,
      vertices: this.gestures.vertexScreens,
      chrome:
        selectionScreenBox && !editingId
          ? { box: selectionScreenBox, handles: this.selectionHandles }
          : null,
    }
  }

  public readonly measure: MeasureText = (text, font) => {
    if (this.deps.measureText) return this.deps.measureText(text, font)
    this.measureContext ??= document.createElement('canvas').getContext('2d')
    this.measureContext.font = font
    return this.measureContext.measureText(text).width
  }

  public toImage(screen: IPoint): IPoint {
    return toImagePoint(screen, this.view)
  }

  public toScreen(image: IPoint): IPoint {
    return toScreenPoint(image, this.view)
  }

  public readonly createId = (): string => BsonObjectId.generate()

  public mount(): void {
    this.keyboardClaim = claimKeyboard()
    window.addEventListener('keydown', this.handleKeyDown)
  }

  @action.bound
  public dispose(): void {
    this.isDisposed = true
    window.removeEventListener('keydown', this.handleKeyDown)
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = null
    this.history.dispose()
    ;(this.image as ImageBitmap)?.close?.()
    this.image = null
  }

  public async load(url: string): Promise<void> {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const source = await readImageSize(blob)
      const size = fitWithin(source.width, source.height)
      const bitmap = await createImageBitmap(
        blob,
        size.width < source.width
          ? {
              imageOrientation: 'from-image',
              resizeWidth: size.width,
              resizeHeight: size.height,
              resizeQuality: 'high',
            }
          : { imageOrientation: 'from-image' },
      )
      if (this.isDisposed) {
        bitmap.close()
        return
      }
      this.setImage(bitmap, fitWithin(bitmap.width, bitmap.height))
    } catch {
      runInAction(() => {
        this.hasLoadFailed = true
      })
    }
  }

  @action.bound
  public setImage(image: CanvasImageSource, size: ISize): void {
    this.image = image
    this.imageSize = size
    this.freezeUnit()
  }

  @action.bound
  public setStageSize(size: ISize): void {
    this.stageSize = size
    this.freezeUnit()
  }

  @action.bound
  public setAvailableStageSize(size: ISize): void {
    this.availableStageSize = size
  }

  @action.bound
  public zoomView(from: IPoint, to: IPoint, factor: number): void {
    if (!this.view) return
    const next = zoomViewport(
      this.view,
      this.stageSize,
      this.imageSize,
      from,
      to,
      factor,
    )
    const fit = fitViewport(this.stageSize, this.imageSize).scale
    this.zoom = next.scale > fit ? next : null
  }

  @action
  private freezeUnit(): void {
    if (this.frozenUnit != null || !this.view) return
    this.frozenUnit = markupUnit(this.imageSize, this.view)
  }

  @action.bound
  private handleKeyDown(event: KeyboardEvent): void {
    handlePhotoMarkupKey(this, event, this.keyboardClaim)
  }

  @action.bound
  public selectTool(tool: MarkupTool): void {
    this.finishTextEditing()
    this.gestures.reset()
    this.activeTool = tool
    if (tool === 'draw') this.style.applyDrawTypeDefaults(this.drawType)
    if (tool === 'shape') this.style.applyShapeDefaults()
    if (tool === 'arrow') this.style.applyLineDefaults()
    if (tool !== 'select') this.selectedId = null
  }

  @action.bound
  public changeShapeKind(kind: ShapeKind): void {
    this.gestures.reset()
    this.shapeKind = kind
  }

  @action.bound
  public changeLineType(type: LineType): void {
    this.gestures.reset()
    this.lineType = type
  }

  @action.bound
  public changeDrawType(type: DrawType): void {
    this.drawType = type
    this.style.applyDrawTypeDefaults(type)
  }

  public newPath(
    kind: IPathMarkup['kind'],
    points: IPoint[],
    id: string = this.createId(),
  ): IPathMarkup {
    const { style } = this
    const heads = defaultArrowheads(this.lineType)
    const isShape = kind === 'shape'
    const isArrow = kind === 'arrow'
    return {
      id,
      kind,
      shapeKind: isShape ? this.shapeKind : undefined,
      lineType: isArrow ? this.lineType : undefined,
      points,
      color: style.color,
      strokeWidth: style.strokeWidth,
      strokeStyle: 'solid',
      fillColor: isShape ? style.fillColor : NO_FILL,
      fillStyle: style.fillStyle,
      arrowheadStart: isArrow ? heads.start : 'none',
      arrowheadEnd: isArrow ? heads.end : 'none',
      borderOverridden: style.borderOverridden,
    }
  }

  @action.bound
  public addMarkup(markup: PhotoMarkup): void {
    this.markups = [...this.markups, markup]
  }

  @action.bound
  public replaceMarkup(markup: PhotoMarkup): void {
    this.markups = this.markups.map(m => (m.id === markup.id ? markup : m))
  }

  @action.bound
  public setMarkups(markups: PhotoMarkup[]): void {
    this.markups = markups
  }

  @action.bound
  public select(id: string | null): void {
    this.selectedId = id
    if (id !== this.editingId) this.editingId = null
  }

  @action.bound
  public showSelectTool(): void {
    this.activeTool = 'select'
  }

  @action.bound
  public finishDiscreteDraw(markup: PhotoMarkup): void {
    this.addMarkup(markup)
    this.history.commit()
    this.activeTool = 'select'
    this.select(markup.id)
  }

  @action.bound
  public finishPolygon(): void {
    this.gestures.finishPolygon()
  }

  @action.bound
  public cancelPolygon(): void {
    this.gestures.cancelVertices()
  }

  @action.bound
  public finishPolyline(): void {
    this.gestures.finishPolyline()
  }

  @action.bound
  public cancelPolyline(): void {
    this.gestures.cancelVertices()
  }

  @action.bound
  public placeText(point: IPoint): void {
    const markup: ITextMarkup = {
      id: this.createId(),
      kind: 'text',
      at: point,
      text: '',
      boxWidth: null,
      ...this.style.textStyle,
    }
    this.addMarkup(markup)
    this.activeTool = 'select'
    this.selectedId = markup.id
    this.editingId = markup.id
  }

  @action.bound
  public startEditing(id: string): void {
    this.selectedId = id
    this.editingId = id
  }

  @action.bound
  public setEditingText(text: string): void {
    const editing = this.editingText
    if (!editing) return
    this.replaceMarkup({ ...editing, text })
    this.history.capture()
  }

  @action.bound
  public finishTextEditing(): void {
    const id = this.editingId
    if (!id) return
    this.editingId = null
    this.discardIfEmpty(id)
    this.history.commit()
  }

  @action.bound
  public handleTextDone(): void {
    this.finishTextEditing()
    this.discardIfEmpty(this.selectedId)
    this.select(null)
  }

  @action
  private discardIfEmpty(id: string | null): void {
    const markup = this.markups.find(m => m.id === id)
    if (markup && !hasContent(markup)) this.removeMarkup(id)
  }

  @action
  private removeMarkup(id: string): void {
    this.markups = this.markups.filter(m => m.id !== id)
    if (this.selectedId === id) this.selectedId = null
    if (this.editingId === id) this.editingId = null
  }

  @action.bound
  public deleteSelected(): void {
    if (!this.selectedId) return
    this.removeMarkup(this.selectedId)
    this.history.commit()
  }

  @action.bound
  public undo(): void {
    this.restore(this.history.undo())
  }

  @action.bound
  public redo(): void {
    this.restore(this.history.redo())
  }

  @action
  private restore(snapshot: PhotoMarkup[] | null): void {
    if (!snapshot) return
    this.gestures.reset()
    this.markups = snapshot
    this.selectedId = null
    this.editingId = null
  }

  @action.bound
  public finishPendingWork(): void {
    if (this.polygonDrawing) this.finishPolygon()
    else if (this.polylineDrawing) this.finishPolyline()
    this.finishTextEditing()
  }

  public async save(): Promise<boolean> {
    if (!this.image || this.frozenUnit == null || this.isSaving) return false
    this.finishPendingWork()
    return this.submit(() =>
      renderPhoto(
        this.image,
        this.imageSize,
        this.markups.filter(hasContent),
        this.unit,
        this.deps.fileName,
        this.deps.createCanvas,
      ),
    )
  }

  public saveOriginal(file: File): Promise<boolean> {
    return this.submit(async () => file)
  }

  private async submit(produce: () => Promise<File>): Promise<boolean> {
    if (this.isSaving) return false
    runInAction(() => {
      this.isSaving = true
    })
    try {
      await this.deps.onSave(await produce())
      return true
    } catch {
      toast.error(Localization.translator.uploadFailed)
      runInAction(() => {
        this.isSaving = false
      })
      return false
    }
  }
}
